import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { reviewsAPI, employerAPI } from '../../services/api';
import ScheduleReviewModal from '../../components/employer/ScheduleReviewModal';
import { toast } from 'sonner';
import { 
  ClipboardList, Star, Clock, Calendar, AlertTriangle, Check, X, User, Plus, Loader2, ChevronRight, CalendarDays
} from 'lucide-react';

const Reviews = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [scheduledReviews, setScheduledReviews] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, scheduledRes, requestsRes, recordsRes] = await Promise.all([
        reviewsAPI.getEmployerReviews(),
        reviewsAPI.getScheduledReviews(),
        reviewsAPI.getReviewRequests(),
        employerAPI.getRecords('acknowledged')
      ]);
      setReviews(reviewsRes.data || []);
      setScheduledReviews(scheduledRes.data || []);
      setReviewRequests(requestsRes.data || []);
      setRecords(recordsRes.data || []);
    } catch (error) {
      console.error('Error fetching reviews data:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const res = await reviewsAPI.acceptRequest(requestId);
      toast.success('Review request accepted');
      // Navigate to create review page with pre-filled data
      navigate(`/employer/reviews/new?employee_id=${res.data.employee_id}&record_id=${res.data.record_id}&cadence=${res.data.preferred_cadence}`);
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await reviewsAPI.declineRequest(requestId, 'Unable to provide review at this time');
      toast.success('Request declined');
      setReviewRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">Pending</span>;
      case 'acknowledged':
        return <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Acknowledged</span>;
      case 'overdue':
        return <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs">Overdue</span>;
      case 'scheduled':
        return <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs">Scheduled</span>;
      default:
        return null;
    }
  };

  const getCadenceBadge = (cadence) => {
    const colors = {
      'one-time': 'bg-blue-500/20 text-blue-400',
      'weekly': 'bg-green-500/20 text-green-400',
      'monthly': 'bg-purple-500/20 text-purple-400',
      'quarterly': 'bg-amber-500/20 text-amber-400',
      'annual': 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${colors[cadence] || colors['one-time']}`}>
        {cadence?.charAt(0).toUpperCase() + cadence?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="employer-reviews">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Performance Reviews</h1>
            <p className="mt-2 text-[#5B6472]">Manage and track employee performance reviews</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/employer/reviews/calendar">
              <Button variant="outline" className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl" data-testid="calendar-view-btn">
                <CalendarDays className="w-4 h-4 mr-2" />
                Calendar
              </Button>
            </Link>
            <Button 
              onClick={() => setShowScheduleModal(true)}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl" 
              data-testid="schedule-review-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Review
            </Button>
          </div>
        </div>

        {/* Review Requests Alert */}
        {reviewRequests.length > 0 && (
          <Card className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 sm:p-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Pending Review Requests ({reviewRequests.length})</h2>
            </div>
            <div className="space-y-3">
              {reviewRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-4 rounded-xl bg-[#FFFFFF] border border-[#E8EDF5]">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                      {request.employee_photo ? (
                        <img src={request.employee_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-[#5B6472]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#0A0F1E]">{request.employee_name || 'Employee'}</p>
                      <p className="text-[#5B6472] text-sm">
                        Requested {getCadenceBadge(request.preferred_cadence)} review
                      </p>
                      {request.note && (
                        <p className="text-[#5B6472] text-xs mt-1 italic">"{request.note}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeclineRequest(request.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`decline-request-${request.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAcceptRequest(request.id)}
                      className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-lg"
                      data-testid={`accept-request-${request.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-in">
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-1 h-auto flex-wrap">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2">
              All Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-lg data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2">
              Scheduled ({scheduledReviews.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2">
              Pending ({reviews.filter(r => r.status === 'pending').length})
            </TabsTrigger>
          </TabsList>

          {/* All Reviews */}
          <TabsContent value="all" className="mt-6">
            {reviews.length > 0 ? (
              <div className="grid gap-4">
                {reviews.map((review) => (
                  <Link key={review.id} to={`/employer/reviews/${review.id}`}>
                    <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5 hover:border-[#1B5AFF]/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                            {review.employee_photo ? (
                              <img src={review.employee_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <User className="w-6 h-6 text-[#5B6472]" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0A0F1E]">{review.employee_name || 'Employee'}</p>
                            <p className="text-[#5B6472] text-sm">{review.employee_title || 'Employee'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getCadenceBadge(review.review_type)}
                              {getStatusBadge(review.status)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-0.5 mb-1 justify-end">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.performance_rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                              />
                            ))}
                          </div>
                          <p className="text-[#5B6472] text-xs">
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#5B6472] ml-4" />
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <ClipboardList className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No reviews yet</h3>
                <p className="text-[#5B6472] mb-4">
                  Create your first performance review for an employee.
                </p>
                {records.length > 0 && (
                  <Link to="/employer/reviews/new">
                    <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Review
                    </Button>
                  </Link>
                )}
              </Card>
            )}
          </TabsContent>

          {/* Scheduled Reviews */}
          <TabsContent value="scheduled" className="mt-6">
            {scheduledReviews.length > 0 ? (
              <div className="grid gap-4">
                {scheduledReviews.map((review) => (
                  <Card 
                    key={review.id} 
                    className={`bg-[#FFFFFF] border rounded-xl p-5 ${
                      review.status === 'overdue' ? 'border-red-500/50' : 'border-[#E8EDF5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          review.status === 'overdue' ? 'bg-red-500/20' : 'bg-[#F5F8FF]'
                        }`}>
                          {review.status === 'overdue' ? (
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                          ) : (
                            <Calendar className="w-6 h-6 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A0F1E]">{review.employee_name || 'Employee'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getCadenceBadge(review.review_type)}
                            {getStatusBadge(review.status)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${review.status === 'overdue' ? 'text-red-400' : 'text-[#0A0F1E]'}`}>
                          Due: {new Date(review.due_date).toLocaleDateString()}
                        </p>
                        <Link to={`/employer/reviews/new?employee_id=${review.employee_id}&record_id=${review.record_id}&cadence=${review.review_type}`}>
                          <Button 
                            size="sm" 
                            className={`mt-2 rounded-lg ${
                              review.status === 'overdue' 
                                ? 'bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF]' 
                                : 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                            }`}
                          >
                            Complete Review
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Calendar className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No scheduled reviews</h3>
                <p className="text-[#5B6472]">
                  Recurring reviews (weekly, monthly, quarterly) will appear here.
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Pending Reviews */}
          <TabsContent value="pending" className="mt-6">
            {reviews.filter(r => r.status === 'pending').length > 0 ? (
              <div className="grid gap-4">
                {reviews.filter(r => r.status === 'pending').map((review) => (
                  <Link key={review.id} to={`/employer/reviews/${review.id}`}>
                    <Card className="bg-[#FFFFFF] border border-amber-500/30 rounded-xl p-5 hover:border-[#1B5AFF]/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                            <Clock className="w-6 h-6 text-amber-400" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#0A0F1E]">{review.employee_name || 'Employee'}</p>
                            <p className="text-[#5B6472] text-sm">Awaiting employee response</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < review.performance_rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                            />
                          ))}
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#5B6472]" />
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">All caught up!</h3>
                <p className="text-[#5B6472]">
                  No reviews awaiting employee acknowledgment.
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Schedule Review Modal */}
        <ScheduleReviewModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={fetchData}
        />
      </div>
    </DashboardLayout>
  );
};

export default Reviews;
